import { GqlError, gql, gqlFetch } from '@/service/gql'
import { type SanitizedResult } from '@amzn/amp-gql'
import { trackFieldsFrag } from '@/service/fragments/trackFieldsFrag'
import { type PlayParams } from '@/service/fragments/playParamsFrag'
import { podcastEpisodeFieldsFrag } from '@/service/fragments/podcastEpisodeFieldsFrag'
import { advertisementFieldsFrag } from '@/service/fragments/advertisementFieldsFrag'
import { getAuth } from '@/auth/authRef'
import {
    ErrorLevel,
    first,
    IllegalArgumentError,
    logDebug,
    logVerbose,
    logWarn,
    type LogTarget,
    type Maybe,
    ReportableError,
} from '@amazon/vinyl-util'
import { UnauthorizedError } from '@/auth/UnauthorizedError'
import { catalogizeLibraryAsinMrn } from '@/util/mrn'
import { playableEntityToPlaylistEntry } from '@/playback/playableEntityToPlaylistEntry'
import type { PlayableEntity } from '@/service/fragments/playableEntityFrag'
import { toast } from '@/components/toast'
import { getStringAsync } from '@/playback/i18n/bundle.generated'
import {
    isExplicitLanguageFilterOn,
    toastExplicitBlocked,
} from '@/explicitLanguageFilter/explicitLanguageFilterService'

const logTarget: LogTarget = {
    logPrefix: 'cloudQueueService',
}

// language=GraphQL
export const queueEntityConnectionFrag = gql(
    `
        fragment queueEntityConnection on QueueEntityConnection @_unmask {
            edges {
                node {
                    ...on Track {
                        ...trackFields
                    }
                    ...on PodcastEpisode {
                        ...podcastEpisodeFields
                    }
                    ...on Advertisement {
                        ...advertisementFields
                    }
                }
                entityReferenceId
                metricId
                capabilities {
                    next { isAllowed metadata { upsell remainingSkips } }
                    previous { isAllowed metadata { upsell remainingSkips } }
                    scrubForward { isAllowed }
                    scrubBackward { isAllowed }
                    loopOne { isAllowed }
                }
            }
            pageInfo {
                hasNextPage
                token
            }
        }
    `,
    [trackFieldsFrag, podcastEpisodeFieldsFrag, advertisementFieldsFrag]
)

// language=GraphQL
const createQueueQuery = gql(
    `
        mutation CreateQueue(
            $id: String!,
            $containerIds: [String],
            $contexts: PlayContextScalar,
            $isAuthenticated: Boolean!,
            $shuffleMode: String,
            $loopMode: String,
            $playbackContinuationMode: String,
            $startAsStation: Boolean,
            $nextLimit: Float
        ) {
            createQueue(playParams:  {
                id: $id,
                containerIds: $containerIds,
                contexts: $contexts,
            },
                playbackOptions:  {
                    shuffleMode: $shuffleMode
                    loopMode: $loopMode
                    playbackContinuationMode: $playbackContinuationMode
                    startAsStation: $startAsStation
                }
            ) {
                queue {
                    id
                    uri
                    current {
                        ...queueEntityConnection
                    }
                    # WORKAROUND: Firefly createQueue anchors "current"
                    # on the container FIRST item, ignoring the
                    # requested playParams.id — clicking track 3 of an
                    # album returns track 1 as the current edge. Fetch
                    # "next(limit)" so the client can search the window
                    # for the requested id and seed the feed on the
                    # right entry (mirrors hornet
                    # CreateLocalQueueWindow.ts workaround).
                    next (limit: $nextLimit) {
                        ...queueEntityConnection
                    }
                    capabilities {
                        shuffle { isAllowed }
                        loopAll { isAllowed }
                        blockQueueMutations { isAllowed }
                    }
                }
                playbackInstruction {
                    startOffsetMs
                }
            }
        }
    `,
    [queueEntityConnectionFrag]
)

export type CreateQueueResult = SanitizedResult<typeof createQueueQuery>

/**
 * GraphQL query for fetching next items in a queue.
 * This query retrieves the next tracks in the queue based on the current entity reference ID.
 */
const getNextQueueItemsQuery = gql(
    `
        query NextTrackInQueue(
            $id: String!,
            $isAuthenticated: Boolean!,
            $nextLimit: Float,
            $currentEntityReferenceId: String
        ) {
            queue(id: $id, currentEntityReferenceId: $currentEntityReferenceId) {
                id
                uri
                next (limit: $nextLimit) {
                    ...queueEntityConnection
                }
            }
        }
    `,
    [queueEntityConnectionFrag]
)

/**
 * Mirror of hornet's FetchRemoteQueue query — used after a queue
 * mutation (shuffle / loop / reorder / remove) to re-pull the full
 * queue window (previous + current + next + capabilities) anchored on the
 * current entity. Hornet runs this with `fetchPolicy: 'network-only'`
 * to bypass the Apollo cache; we drop the `api-cache` server flag
 * for the same reason via `featureFlags: 'use-ups'`.
 */
const fetchRemoteQueueQuery = gql(
    `
        query FetchRemoteQueue(
            $id: String!,
            $isAuthenticated: Boolean!,
            $nextLimit: Float,
            $currentEntityReferenceId: String
        ) {
            queue(id: $id, currentEntityReferenceId: $currentEntityReferenceId) {
                id
                uri
                current {
                    ...queueEntityConnection
                }
                next (limit: $nextLimit) {
                    ...queueEntityConnection
                }
                previous {
                    ...queueEntityConnection
                }
                capabilities {
                    shuffle { isAllowed }
                    loopAll { isAllowed }
                    blockQueueMutations { isAllowed }
                }
            }
        }
    `,
    [queueEntityConnectionFrag]
)

export type FetchRemoteQueueResult = SanitizedResult<typeof fetchRemoteQueueQuery>

const QUEUE_REFETCH_PREFETCH = 1

/**
 * Re-pulls the live cloud queue window from the gateway. Mirrors
 * hornet's `fetchRemoteQueue` — used after mutations that change the
 * server-side ordering or membership (shuffle / loop / reorder /
 * remove) so the local feed is rebuilt with the new edges. Bypasses
 * Firefly's `api-cache` flag.
 */
export async function fetchRemoteQueue(
    queueId: string,
    currentEntityReferenceId?: string | null
): Promise<FetchRemoteQueueResult> {
    if (!getAuth().isSignedIn()) throw new UnauthorizedError()
    logVerbose(
        logTarget,
        `[preload-trace] fetchRemoteQueue: queueId=${queueId} ref=${currentEntityReferenceId ?? ''}`
    )
    logDebug(logTarget, 'fetchRemoteQueue', { queueId, currentEntityReferenceId })
    return gqlFetch(
        fetchRemoteQueueQuery,
        {
            id: queueId,
            isAuthenticated: true,
            nextLimit: QUEUE_REFETCH_PREFETCH,
            currentEntityReferenceId: currentEntityReferenceId ?? null,
        },
        {
            // network-only equivalent: drop the api-cache flag so
            // Firefly walks UPS fresh. Without this the gateway
            // returns the pre-mutation window keyed on
            // (queueId, ref) and our local rebuild misses the
            // post-shuffle ordering.
            featureFlags: 'use-ups',
            rejectPartialResponse: true,
        }
    )
}

/**
 * GraphQL query for fetching previous items in a queue.
 * This query retrieves the previous tracks in the queue based on the current entity reference ID.
 */
const getPreviousQueueItemsQuery = gql(
    `
        query PreviousTrackInQueue(
            $id: String!,
            $isAuthenticated: Boolean!,
            $previousLimit: Float,
            $currentEntityReferenceId: String
        ) {
            queue(id: $id, currentEntityReferenceId: $currentEntityReferenceId) {
                id
                uri
                previous (limit: $previousLimit) {
                    ...queueEntityConnection
                }
            }
        }
    `,
    [queueEntityConnectionFrag]
)

export type NextTrackInQueueResult = SanitizedResult<typeof getNextQueueItemsQuery>

export type PreviousTrackInQueueResult = SanitizedResult<typeof getPreviousQueueItemsQuery>

const QUEUE_PREFETCH_COUNT = 1

// Firefly's `previous` connection rejects a limit outside 1..10 ("Invalid value
// for limit. Allowed value is between 1 and 10"), unlike `view`/`next`. Callers
// that want a full page of already-played rows are clamped to this ceiling.
export const MAX_PREVIOUS_QUEUE_LIMIT = 10

/**
 * LOOP_OFF - No looping.
 * LOOP_ONE - Loops a single track.
 * LOOP_ALL - When the playlist reaches its end, starts over at the first track.
 */
export type LoopMode = 'LOOP_OFF' | 'LOOP_ONE' | 'LOOP_ALL'

export type ShuffleMode = 'SHUFFLE_ON' | 'SHUFFLE_OFF'

export type PlaybackContinuationMode = 'AUTOPLAY_ON' | 'AUTOPLAY_OFF'

export interface CreateCloudQueuePlayOptions {
    /**
     * Sets the loop mode, default is OFF.
     */
    readonly loop?: Maybe<LoopMode>

    /**
     * Shuffle mode.
     */
    readonly shuffle?: Maybe<ShuffleMode>

    /**
     * Start playback using station mode. This allows user start playing
     * similar content.
     */
    readonly startAsStation?: Maybe<boolean>

    /**
     * Starts a new queue when the first ends. (I think)
     */
    readonly playbackContinuationMode?: Maybe<PlaybackContinuationMode>

    /**
     * Play-context flags forwarded on the queue's playParams (the
     * `PlayContextScalar` JSON blob). GH sets `{ AUTOPLAY: true }` when
     * it re-seeds a continuation queue at end-of-queue so the gateway
     * knows the new queue is machine-initiated, not user-picked.
     */
    readonly contexts?: Maybe<Record<string, boolean>>
}

export interface CreateCloudQueueOptions extends CreateCloudQueuePlayOptions {
    /**
     * Required parameters to begin playback.
     */
    readonly playParams: PlayParams

    /**
     * The id of the queue once it's been created. Set by
     * CloudQueueController after createQueue resolves so observers of
     * `cloudQueueOptions` can read the live queue id (e.g. to call
     * fetchQueueView for an in-page queue list).
     */
    readonly queueId?: Maybe<string>
}

/**
 * Audible-owned MRN media types (audiobook, audioplay chapter/episode/series).
 * The Firefly cloud queue rejects these with "unsupported play params" —
 * we don't ship the Audible SDK, so surface a friendly toast instead of
 * bubbling the gateway error.
 */
const AUDIBLE_MEDIA_TYPES = ['audiobook', 'audioplay', 'audioplaychapter', 'audioplayepisode']

function isAudiblePlayParamsId(id: string): boolean {
    const lower = id.toLowerCase()
    return AUDIBLE_MEDIA_TYPES.some((t) => lower.includes(`:${t}:`))
}

/**
 * Firefly rejects a queue whose every entity was dropped during
 * population — the common cause is playing a container (album /
 * playlist) whose tracks are ALL masked by the explicit-language
 * filter, leaving nothing to enqueue. It surfaces as a GraphQL error
 * whose `extensions.code` is `SERVICE_ERROR` and whose
 * `extensions.payload` names the InitiateQueue operation on
 * MusicCloudQueueService (message: "Entity unavailable after
 * population"). We match on the structured payload, not the brittle
 * message string — mirrors the concurrency-error detection in
 * `deviceStreamingService.ts`.
 */
function isEntityUnavailableAfterPopulation(error: unknown): boolean {
    if (!(error instanceof GqlError)) return false
    return error.errors.some((err) => {
        const payload = err.extensions?.payload as
            | Maybe<{ operation?: string; serviceName?: string }>
            | undefined
        return (
            err.extensions?.code === 'SERVICE_ERROR' &&
            payload?.operation === 'InitiateQueue' &&
            payload.serviceName === 'MusicCloudQueueService'
        )
    })
}

/**
 * Title of the track a createQueue window resolves to — the entry that becomes
 * current, so the createQueue timing bar shares the RESOLVED track's lane rather
 * than the clicked container's title (the disassociation this fixes). Mirrors
 * CloudQueueController's seed search: the requested id found within the window,
 * else the anchored current. A track the gateway drops from the window entirely
 * resolves via the caller-supplied entity in setQueue, which this can't see, so
 * that rare case falls back to the anchored current (a minor residual). Cheap:
 * runs once per play, only when the createQueue bar draws.
 */
export function resolvedQueueTrackTitle(
    result: CreateQueueResult,
    playParamsId?: Maybe<string>
): string | undefined {
    const queue = result.createQueue?.queue
    const edges = [...(queue?.current?.edges ?? []), ...(queue?.next?.edges ?? [])]
    const entries = edges
        .map((e) => playableEntityToPlaylistEntry(e.node as PlayableEntity))
        .filter((e): e is NonNullable<typeof e> => e != null)
    const requestedId = playParamsId ? catalogizeLibraryAsinMrn(playParamsId) : playParamsId
    const found = requestedId
        ? entries.find(
              (e) =>
                  e.entity.playParams?.id === requestedId ||
                  e.entity.uri === requestedId ||
                  e.id === requestedId
          )
        : undefined
    return (found ?? first(entries))?.title
}

export async function createCloudQueue(
    options: CreateCloudQueueOptions
): Promise<CreateQueueResult> {
    if (!getAuth().isSignedIn()) throw new UnauthorizedError()
    logVerbose(logTarget, `[preload-trace] createCloudQueue: entry=${options.playParams.id}`)
    logDebug(logTarget, 'createCloudQueue', options)
    const { playParams } = options
    if (!playParams.id) throw new IllegalArgumentError('playParams missing id')
    if (isAudiblePlayParamsId(playParams.id)) {
        toast(getStringAsync('dmusic_playback_failed_audiobook_toast'))
        // Silent so the errorHandler doesn't stack a second raw toast on top.
        throw new ReportableError('audible playback not supported', undefined, ErrorLevel.SILENT)
    }
    // Catalog-back a library-scoped purchase / save so the gateway routes it to
    // the streaming provider instead of the owned-content provider (which
    // rejects InitiateQueue for a catalog-backed asin). Containers too, so a
    // library album/playlist context resolves the same way.
    const id = catalogizeLibraryAsinMrn(playParams.id)
    const containerIds = playParams.containerIds?.map(catalogizeLibraryAsinMrn) ?? []
    try {
        return await gqlFetch(
            createQueueQuery,
            {
                id,
                containerIds,
                // PlayContextScalar is a JSON-string scalar on the wire; GH
                // serializes the flag object before sending. Omit when unset.
                contexts: options.contexts ? JSON.stringify(options.contexts) : null,
                isAuthenticated: true,
                shuffleMode: options.shuffle ?? 'SHUFFLE_OFF',
                // Honor the caller's explicit continuation mode; otherwise OFF.
                // The Autoplay *setting* is NOT consulted here — mirroring GH,
                // where `createRemoteQueue` only forwards the per-call mode and
                // the setting drives a separate end-of-queue re-seed (see
                // @/playback/autoplayContinuation). AUTOPLAY_ON at create time is
                // a per-tile opt-in (station-style continuation), not the setting.
                playbackContinuationMode: options.playbackContinuationMode ?? 'AUTOPLAY_OFF',
                loopMode: options.loop ?? 'LOOP_OFF',
                startAsStation: options.startAsStation ?? false,
                nextLimit: QUEUE_PREFETCH_COUNT,
            },
            {
                // If CloudQueue contains errors, do not accept partial data:
                rejectPartialResponse: true,
                isRetryableError: () => true, // CQ times out frequently
                // Name the createQueue span's lane after the track the queue
                // actually resolves to (known only post-response), so it shares
                // the resolved track's journey lane, not the requested id's.
                timingTrack: (result: unknown) =>
                    resolvedQueueTrackTitle(result as CreateQueueResult, playParams.id),
            }
        )
    } catch (error) {
        if (isEntityUnavailableAfterPopulation(error)) {
            // Reuse the explicit-filter toast when the filter is what emptied
            // the queue (consistent with the per-track masked-play toast);
            // otherwise the container is genuinely unplayable right now.
            if (isExplicitLanguageFilterOn()) {
                toastExplicitBlocked()
            } else {
                toast(getStringAsync('dmusic_playback_failed_generic_toast'))
            }
            logWarn(logTarget, 'createQueue: entity unavailable after population')
            // Silent so the errorHandler doesn't stack a second raw toast — the
            // toast above IS the handling. The rejection still aborts the
            // caller's setQueue chain so the player never enters a stuck load.
            throw new ReportableError(
                'queue entity unavailable after population',
                undefined,
                ErrorLevel.SILENT
            )
        }
        throw error
    }
}

/**
 * MRN that seeds a continuation queue off a finished one. Mirrors
 * hornet's `mrn:1.0:queue:queueid:uuid:<id>` — the recommender walks its
 * similar-tracks graph off this id. Exported so the end-of-queue binding
 * can build matching `setQueue` playParams without duplicating the shape.
 */
export function continuationQueueSeedId(queueId: string): string {
    return `mrn:1.0:queue:queueid:uuid:${queueId}`
}

/** PlayParams that seed a continuation queue off the finished `queueId`. */
export function continuationQueuePlayParams(queueId: string): PlayParams {
    return { id: continuationQueueSeedId(queueId), containerIds: [], selections: [] }
}

/**
 * Re-seed a fresh cloud queue from a finished one so playback continues
 * with machine-picked similar tracks — GH's autoplay behavior.
 *
 * Mirrors hornet's `_handlePlayerQueueEnded`: when the player runs to the
 * end of the last remote item and the Autoplay setting is on, it starts a
 * new queue seeded on the finished queue's MRN and tagged with
 * `contexts: { AUTOPLAY: true }` so the gateway knows the queue is
 * machine-initiated. The gateway returns a fresh window of continuation
 * tracks.
 */
export function createAutoplayContinuationQueue(queueId: string): Promise<CreateQueueResult> {
    return createCloudQueue({
        playParams: continuationQueuePlayParams(queueId),
        contexts: { AUTOPLAY: true },
    })
}

// Mirrors hornet's `setQueueShuffleMode` / `setQueueLoopMode`
// mutations. Without flipping shuffle/loop server-side the cached
// next-lookup re-fetches in linear order; the local
// `shuffleEnabled` / `repeatMode` flags only nudge the controller's
// cached next, not the queue's own ordering.
const setQueueShuffleModeMutation = gql(
    `
        mutation setQueueShuffleMode($queueId: String!, $shuffleMode: String!) {
            setQueueShuffleMode(id: $queueId, shuffleMode: $shuffleMode) {
                status
            }
        }
    `,
    []
)

const setQueueLoopModeMutation = gql(
    `
        mutation setQueueLoopMode($queueId: String!, $loopMode: String!) {
            setQueueLoopMode(id: $queueId, loopMode: $loopMode) {
                status
            }
        }
    `,
    []
)

// reportDevicePlayback tells the gateway which entry the user is
// currently parked on inside the cloud queue. The shuffle / loop
// mutations consult that anchor to compute the new ordering —
// without a recent report they fail with
// "Cannot get currentEntityReferenceId".
//
// Variable shape mirrors hornet's ReportDevicePlayback verbatim
// (`reportDevicePlaybackToRemoteQueue.ts`): `clientTimestampInMs`
// and `progressInMs` are non-null Float!, the timestamp is
// Date.now() at call time. Earlier we passed those as nullable
// optionals which the gateway silently rejected — leaving the
// anchor unset and reproducing the original error.
const reportDevicePlaybackMutation = gql(
    `
        mutation reportDevicePlayback(
            $queueId: String!
            $clientTimestampInMs: Float!
            $progressInMs: Float!
            $entityReferenceId: String!
            $playbackState: String!
        ) {
            reportDevicePlayback(
                id: $queueId
                currentEntityState: {
                    clientTimestampInMs: $clientTimestampInMs
                    entityReferenceId: $entityReferenceId
                    progressInMs: $progressInMs
                }
                playbackState: $playbackState
            ) {
                status
            }
        }
    `,
    []
)

// Mirrors hornet's `addToQueue` mutation: append a single entity
// (track or episode) to the end of the live queue. The empty
// fragment-list arg tells gql.tada this mutation is intentionally
// not pulling in `queueEntityConnection` (the file-level frag).
const addToQueueMutation = gql(
    `
        mutation addToQueue($queueId: String!, $entityId: String!) {
            addToQueue(id: $queueId, playParams: { id: $entityId }) {
                status
            }
        }
    `,
    []
)

// Mirrors hornet's `insertIntoQueue` mutation: insert relative to a
// known anchor (today's `current` entity) with placement BEFORE /
// AFTER. Used to seed Play Next.
const insertIntoQueueMutation = gql(
    `
        mutation insertIntoQueue(
            $queueId: String!
            $entityId: String!
            $placement: String!
            $anchorEntityReferenceId: String!
        ) {
            insertIntoQueue(
                id: $queueId
                placement: $placement
                playParams: { id: $entityId }
                entityReferenceId: $anchorEntityReferenceId
            ) {
                status
            }
        }
    `,
    []
)

// Queue mutations return a `{ status }` payload; a non-SUCCESS status
// is a gateway-level failure the caller must surface (with
// rejectPartialResponse a Firefly error already throws first, so a
// null status here means the mutation ran but reported failure).
function assertMutationSuccess(mutation: string, status: Maybe<string>): void {
    if (status !== 'SUCCESS') {
        throw new Error(`${mutation} returned status=${status ?? 'null'}`)
    }
}

export async function addEntityToCloudQueue(queueId: string, entityId: string): Promise<void> {
    if (!getAuth().isSignedIn()) throw new UnauthorizedError()
    logDebug(logTarget, 'addEntityToCloudQueue', { queueId, entityId })
    // rejectPartialResponse so a Firefly error surfaces as a thrown
    // GqlError instead of being swallowed into a `null` status — the toast
    // then reads the real reason rather than "returned null". isPartialSuccess
    // keeps a SUCCESS status from being masked by an unrelated sibling error.
    const result = await gqlFetch(
        addToQueueMutation,
        { queueId, entityId },
        {
            rejectPartialResponse: true,
            isPartialSuccess: (data) => data?.addToQueue?.status === 'SUCCESS',
        }
    )
    const status = result.addToQueue?.status
    logDebug(logTarget, 'addEntityToCloudQueue result', { status })
    assertMutationSuccess('addToQueue', status)
}

/**
 * Inserts an entity AFTER the given anchor reference (the anchor is
 * typically the current playing entity's ref). Powers "Play Next".
 */
export async function insertEntityIntoCloudQueue(
    queueId: string,
    entityId: string,
    anchorEntityReferenceId: string,
    placement: 'AFTER' | 'BEFORE' = 'AFTER'
): Promise<void> {
    if (!getAuth().isSignedIn()) throw new UnauthorizedError()
    logDebug(logTarget, 'insertEntityIntoCloudQueue', {
        queueId,
        entityId,
        anchorEntityReferenceId,
        placement,
    })
    // insertIntoQueue resolves the insert position against this device's
    // reported playback state; without a recent report the gateway fails with
    // "Failed to retrieve device playback state". Anchor the cursor on the
    // insert anchor first — the awaited report lands before the mutation reads
    // it, closing the race after a fresh createQueue and the gap after a
    // reload-restore that parks the queue paused (never reports PLAYING).
    await anchorQueueCursor(queueId, anchorEntityReferenceId)
    const result = await gqlFetch(
        insertIntoQueueMutation,
        {
            queueId,
            entityId,
            anchorEntityReferenceId,
            placement,
        },
        {
            rejectPartialResponse: true,
            isPartialSuccess: (data) => data?.insertIntoQueue?.status === 'SUCCESS',
        }
    )
    const status = result.insertIntoQueue?.status
    logDebug(logTarget, 'insertEntityIntoCloudQueue result', { status })
    assertMutationSuccess('insertIntoQueue', status)
}

/**
 * Tells the cloud queue gateway which entry the user is parked on.
 * Required prelude for setQueueShuffleMode / setQueueLoopMode —
 * those mutations error with "Cannot get currentEntityReferenceId"
 * unless a recent report has anchored the queue's cursor.
 *
 * `entityReferenceId` — the live entry the controller is on (the
 * `entityReferenceId` stamped on PlaylistEntry by the cloud queue
 * feed). `progressInMs` is best-effort.
 */
export async function reportCloudQueuePlayback(input: {
    readonly queueId: string
    readonly entityReferenceId: string
    readonly progressInMs?: number
    readonly playbackState?: 'PLAYING' | 'STOPPED' | 'ENDED' | 'DETACHED'
}): Promise<void> {
    if (!getAuth().isSignedIn()) throw new UnauthorizedError()
    logDebug(logTarget, 'reportCloudQueuePlayback', input)
    // Both timestamp and progress are non-nullable Float! per the
    // schema. Default progress to 0 when the caller doesn't have a
    // live read (e.g. just after a track change before audio has
    // started).
    const result = await gqlFetch(
        reportDevicePlaybackMutation,
        {
            queueId: input.queueId,
            entityReferenceId: input.entityReferenceId,
            clientTimestampInMs: Date.now(),
            progressInMs: input.progressInMs ?? 0,
            playbackState: input.playbackState ?? 'PLAYING',
        },
        { rejectPartialResponse: true }
    )
    const status = result.reportDevicePlayback?.status
    logDebug(logTarget, 'reportCloudQueuePlayback result', { status })
}

// Anchor the gateway cursor before a shuffle / loop / insert mutation —
// those need a known current entity (this device's reported playback
// state) to compute the new ordering relative to the user's play
// position. Best-effort: the mutation surfaces its own error if the
// anchor failed.
async function anchorQueueCursor(
    queueId: string,
    currentEntityReferenceId?: string | null
): Promise<void> {
    if (!currentEntityReferenceId) return
    await reportCloudQueuePlayback({ queueId, entityReferenceId: currentEntityReferenceId }).catch(
        () => {}
    )
}

export async function setCloudQueueShuffleMode(
    queueId: string,
    shuffleMode: ShuffleMode,
    currentEntityReferenceId?: string | null
): Promise<void> {
    if (!getAuth().isSignedIn()) throw new UnauthorizedError()
    logDebug(logTarget, 'setCloudQueueShuffleMode', { queueId, shuffleMode })
    await anchorQueueCursor(queueId, currentEntityReferenceId)
    const result = await gqlFetch(
        setQueueShuffleModeMutation,
        { queueId, shuffleMode },
        { rejectPartialResponse: true }
    )
    const status = result.setQueueShuffleMode?.status
    logDebug(logTarget, 'setCloudQueueShuffleMode result', { status })
    assertMutationSuccess('setQueueShuffleMode', status)
}

export async function setCloudQueueLoopMode(
    queueId: string,
    loopMode: LoopMode,
    currentEntityReferenceId?: string | null
): Promise<void> {
    if (!getAuth().isSignedIn()) throw new UnauthorizedError()
    logDebug(logTarget, 'setCloudQueueLoopMode', { queueId, loopMode })
    await anchorQueueCursor(queueId, currentEntityReferenceId)
    const result = await gqlFetch(
        setQueueLoopModeMutation,
        { queueId, loopMode },
        { rejectPartialResponse: true }
    )
    const status = result.setQueueLoopMode?.status
    logDebug(logTarget, 'setCloudQueueLoopMode result', { status })
    assertMutationSuccess('setQueueLoopMode', status)
}

/**
 * Fetches next items in the cloud queue based on the current reference ID.
 *
 * @param id - The ID of the queue to fetch items from
 * @param referenceId - The reference ID of the current item in the queue
 * @returns A promise that resolves to the next tracks in the queue
 *
 * This function makes a GraphQL request to fetch a batch of next tracks
 * in the cloud queue relative to the provided reference ID. It uses the
 * QUEUE_PREFETCH_COUNT constant to determine how many items to fetch.
 */
export async function getNextCloudQueueItems(
    id: string,
    referenceId: string
): Promise<NextTrackInQueueResult> {
    logDebug(logTarget, 'getNextCloudQueueItems', id, referenceId)

    const isAuthenticated = getAuth().isSignedIn()
    return gqlFetch(
        getNextQueueItemsQuery,
        {
            currentEntityReferenceId: referenceId,
            isAuthenticated,
            nextLimit: QUEUE_PREFETCH_COUNT,
            id,
        },
        {
            // If CloudQueue contains errors, do not accept partial data:
            rejectPartialResponse: true,
        }
    )
}

/**
 * Fetches previous items in the cloud queue based on the current reference ID.
 *
 * @param id - The ID of the queue to fetch items from
 * @param referenceId - The reference ID of the current item in the queue
 * @returns A promise that resolves to the previous tracks in the queue
 *
 * This function makes a GraphQL request to fetch a batch of previous tracks
 * in the cloud queue relative to the provided reference ID. `limit` defaults to
 * QUEUE_PREFETCH_COUNT (the single immediately-previous entry skip-back needs);
 * the queue-view history strip passes a larger page to list already-played rows.
 * `limit` is clamped to {@link MAX_PREVIOUS_QUEUE_LIMIT} — the gateway rejects the
 * whole query for a larger value.
 */
export async function getPreviousCloudQueueItems(
    id: string,
    referenceId: string,
    limit?: number
): Promise<PreviousTrackInQueueResult> {
    logDebug(logTarget, 'getPreviousCloudQueueItems', id, referenceId)

    const isAuthenticated = getAuth().isSignedIn()
    return gqlFetch(
        getPreviousQueueItemsQuery,
        {
            currentEntityReferenceId: referenceId,
            isAuthenticated,
            previousLimit: Math.min(limit ?? QUEUE_PREFETCH_COUNT, MAX_PREVIOUS_QUEUE_LIMIT),
            id,
        },
        {
            // If CloudQueue contains errors, do not accept partial data:
            rejectPartialResponse: true,
        }
    )
}
